// src/util/messages_api_util.js
import { softExpire } from "./soft_expire";

/**
 * Always return "Bearer <jwt>" if token exists.
 * localStorage may contain either "Bearer xxx" or raw jwt.
 */
const getBearerToken = () => {
  const stored = localStorage.getItem("jwtToken");
  if (!stored) return null;
  return stored.startsWith("Bearer ") ? stored : `Bearer ${stored}`;
};

// JSON requests (normal API calls)
const jsonAuthHeaders = () => {
  const bearer = getBearerToken();
  const headers = { "Content-Type": "application/json" };
  if (bearer) headers.Authorization = bearer;
  return headers;
};

// FormData/file uploads (NO manual Content-Type)
const tokenOnlyHeaders = () => {
  const bearer = getBearerToken();
  const headers = {};
  if (bearer) headers.Authorization = bearer;
  return headers;
};

// -------- safe JSON helper --------
const safeJson = async (res) => {
  // ✅ If we’re already expired, avoid extra noise
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  // ✅ Handle 401 gracefully: soft-expire once, then stop.
  if (res.status === 401) {
    softExpire("401");
    return null; // IMPORTANT: don't throw -> prevents Uncaught (in promise)
  }

  const text = await res.text();

  // 204 No Content or empty → null
  if (!text) {
    if (!res.ok) {
      console.error("API error:", res.status, res.statusText);
      throw new Error(res.statusText || "Request failed");
    }
    return null;
  }

  let payload = text;
  try {
    payload = JSON.parse(text);
  } catch (e) {
    // leave as string
  }

  if (!res.ok) {
    console.error("API error:", res.status, payload);
    throw new Error(
      typeof payload === "string" ? payload : payload.error || payload.message || "Request failed"
    );
  }

  return payload;
};

// ==================================
// FILE / MEDIA UPLOADS
// ==================================

// Upload a media file (image/video) for messaging → S3 via /api/files/message-media
export const uploadMessageMedia = async (file) => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const formData = new FormData();
  formData.append("file", file);

  const headers = tokenOnlyHeaders();
  delete headers["Content-Type"];

  const res = await fetch("/api/files/message-media", {
    method: "POST",
    headers,
    body: formData,
  });

  return safeJson(res);
};

// ==================================
// MESSAGE THREAD APIS
// ==================================

// STUDENT: get or create their own thread, then return thread
export const createOrFetchMyThread = async () => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch("/api/messages/my-thread", {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify({}),
  });

  return safeJson(res);
};

// MASTER: fetch all threads for this master/grandmaster
export const fetchThreads = async () => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch("/api/messages/threads", {
    method: "GET",
    headers: jsonAuthHeaders(),
  });

  return safeJson(res);
};

// Get messages for a specific thread
export const fetchThreadMessages = async (threadId) => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch(`/api/messages/thread/${threadId}/messages`, {
    method: "GET",
    headers: jsonAuthHeaders(),
  });

  return safeJson(res);
};

// Send a message in a thread (supports text + optional media)
export const sendThreadMessage = async (threadId, text, mediaUrl = null, mediaType = null) => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch(`/api/messages/thread/${threadId}/send`, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify({ text, mediaUrl, mediaType }),
  });

  return safeJson(res);
};

// Mark all messages in a thread as read for the current user
export const markThreadRead = async (threadId) => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch(`/api/messages/thread/${threadId}/mark-read`, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify({}),
  });

  return safeJson(res);
};

// Mark thread as unread (master/grandmaster)
export const markThreadUnread = async (threadId) => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch(`/api/messages/thread/${threadId}/mark-unread`, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify({}),
  });

  return safeJson(res);
};

// React to a message
export const reactToMessageApi = async (messageId, emoji) => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch(`/api/messages/message/${messageId}/react`, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify({ emoji }),
  });

  return safeJson(res);
};

// ✅ MASTER/GRANDMASTER: get or create a master↔student thread
export const createOrFetchMasterThreadWithStudent = async (studentId) => {
  if (window.__COSMETINK_SOFT_EXPIRED__) return null;

  const res = await fetch(`/api/messages/master-thread/${studentId}`, {
    method: "GET",
    headers: jsonAuthHeaders(),
  });

  return safeJson(res);
};
